/**
 * @file emailDelivery.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-10-30
 * @description What became of a sent email, in words, and which outcomes are
 *              a DELIVERY PROBLEM — the one rule for Plan History's red
 *              triangles and the planning boards' "email bounced" notice
 *              (Cam, 30 Oct 2026: "if an email bounces or fails to delivery
 *              for any reason, a warning triangle icon shows in transport
 *              planning header").
 *
 *   The statuses are the ones audit.email_messages holds
 *   (migrations/create_email_audit_tables.sql): 'sent' when it left us,
 *   'failed' when the mail server would not take it (emailService
 *   .logEmailMessage), and SES's outcome rolled up by routes/webhooks.js —
 *   'delivered', 'bounced', 'complained', 'rejected'. 'complaint' is an older
 *   spelling the screens already knew. Every final non-delivery is a
 *   problem; 'sent' (still on its way) and 'delivered' are not, and neither
 *   is a status nobody recognises.
 *
 *   The server's utils/emailDelivery.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

export const DELIVERY_STATUSES = {
  delivered: { label: 'Delivered', tone: 'ok', icon: 'check_circle', problem: false },
  sent: { label: 'Awaiting delivery', tone: 'wait', icon: 'schedule', problem: false },
  bounced: { label: 'Bounced', tone: 'bad', icon: 'error', problem: true, verb: 'bounced' },
  complained: { label: 'Marked as spam', tone: 'bad', icon: 'report', problem: true, verb: 'marked as spam' },
  complaint: { label: 'Marked as spam', tone: 'bad', icon: 'report', problem: true, verb: 'marked as spam' },
  rejected: { label: 'Rejected', tone: 'bad', icon: 'block', problem: true, verb: 'rejected' },
  failed: { label: 'Failed to send', tone: 'bad', icon: 'error', problem: true, verb: 'failed to send' },
  unknown: { label: 'Not tracked', tone: 'wait', icon: 'help', problem: false }
};

/** The transport emails the planning boards' notices are about. */
export const TRANSPORT_EMAIL_TYPES = ['driver_paperwork', 'supplier_order_summary', 'transport_office_summary'];

const norm = (status) => String(status == null ? '' : status).trim().toLowerCase();

/** How a status reads: { label, tone ('ok' | 'wait' | 'bad'), icon, problem, verb? }. */
export function deliveryStatus(status) {
  const s = norm(status);
  if (!s) return DELIVERY_STATUSES.sent;
  return DELIVERY_STATUSES[s] || { label: s.charAt(0).toUpperCase() + s.slice(1), tone: 'wait', icon: 'help', problem: false };
}

/** Bounced, marked as spam, rejected or failed to send: it did not reach them. */
export function isDeliveryProblem(status) {
  return !!DELIVERY_STATUSES[norm(status)]?.problem;
}

/**
 * The headline for a set of problem emails ({ status }): "1 email bounced",
 * "2 emails failed to send" when they share a status, else
 * "3 emails not delivered".
 */
export function problemsHeadline(problems) {
  const list = Array.isArray(problems) ? problems : [];
  if (!list.length) return '';
  const n = list.length;
  const noun = n === 1 ? 'email' : 'emails';
  const verbs = [...new Set(list.map(p => deliveryStatus(p.status).verb || 'not delivered'))];
  return `${n} ${noun} ${verbs.length === 1 ? verbs[0] : 'not delivered'}`;
}

/**
 * Which emails they were, short: "Driver paperwork", or the first kind and
 * how many more ("Mill order summary + 1 more").
 */
export function problemsKinds(problems) {
  const list = Array.isArray(problems) ? problems : [];
  const kinds = [...new Set(list.map(p => p.label || 'Email'))];
  if (!kinds.length) return '';
  return kinds.length === 1 ? kinds[0] : `${kinds[0]} + ${kinds.length - 1} more`;
}
